import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { Intent } from '@blueprintjs/core';
import { FormSwitch, FormTextInput, FormLocationSelector, FormNumericInput, FormReadonly, FormDateInput } from 'components/form-fields';
import { NotificationToaster, NotificationInline, AlertUnsavedData, AlertConfirm } from 'components/notifications';
import { ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { UserHasAccess } from 'components/user-access';
import { EarnedRecognitionService, LocationService } from 'services';
import { useDirtyData } from 'hooks/useDirtyData';
import isIsoPeriodStart from 'utils/isIsoPeriodStart';

const dvsaErNumberMaxLength = 50;
const minFrequencyWeeks = 1;
const maxFrequencyWeeks = 13;
const dateDisplayFormat = 'DD/MM/YYYY';
const dvsaErStartDateFormat = 'YYYY-MM-DD';
const dvsaErStartDateMinYears = 10;

function formatPeriodStart(dateValue) {
    if (dateValue == null || dateValue === '') {
        return '';
    }

    return moment(dateValue).format(dateDisplayFormat);
}

// Mirror the runtime normalisation the toggle/number effects apply, so the loaded baseline is already
// consistent — otherwise those effects diverge settings from originalSettings on load and raise a
// false "unsaved changes" flag (bug 168722).
function normaliseSettings(values) {
    const normalised = { ...values };
    if (!normalised.sendDriverData && !normalised.sendMaintenanceData) {
        normalised.dvsaErNumber = '';
    }
    if (normalised.dvsaErNumber === '') {
        normalised.dvsaErStartDate = null;
    }
    return normalised;
}

export function AccountEarnedRecognition() {
    const defaultSettings = {
        sendDriverData: false,
        sendMaintenanceData: false,
        erNumber: '',
        locations: [],
        dvsaErNumber: '',
        dvsaErStartDate: null,
        oLicenceInspectionFrequencyWeeksVehicleDefault: null,
        oLicenceInspectionFrequencyWeeksTrailerDefault: null
    };

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(defaultSettings);
    const [originalSettings, setOriginalSettings] = useState(defaultSettings);
    const [customLocations, setCustomLocations] = useState(null);
    const [useHierarchy, setUseHierarchy] = useState(false);

    // Period-start dates loaded from the backend; never mutated after the initial load.
    // Two fields per data type: the stored (already-locked-in) value, and the suggested value to use when the user enables the toggle/field during this session.
    const [periodStartDates, setPeriodStartDates] = useState({
        driverDataPeriodStart: null,
        maintenanceDataPeriodStart: null,
        erNumberPeriodStart: null,
        driverDataStartPeriodStart: null,
        maintenanceDataStartPeriodStart: null,
        erIdStartPeriodStart: null
    });

    // Maintenance KPI validation result from CheckMS — drives toggle lock + helper text.
    const [maintenanceKpiValidation, setMaintenanceKpiValidation] = useState(null);

    // Capture the originally-saved values at page load so we can:
    //   - decide whether the save-time confirmation dialog is needed (a previously-active setting is being removed)
    //   - decide whether a pre-save KPI re-validation is needed
    const [initialDriverDataEnabled, setInitialDriverDataEnabled] = useState(false);
    const [initialMaintenanceDataEnabled, setInitialMaintenanceDataEnabled] = useState(false);
    const [initialErNumber, setInitialErNumber] = useState('');
    const [initialDvsaErNumber, setInitialDvsaErNumber] = useState('');
    const [initialVehicleFrequency, setInitialVehicleFrequency] = useState(null);
    const [initialTrailerFrequency, setInitialTrailerFrequency] = useState(null);

    // Pending confirmation state. When set, the AlertConfirm dialog is open and the
    // save only proceeds if the user confirms.
    const [pendingConfirmation, setPendingConfirmation] = useState(null);

    const isDataDirty = useDirtyData(originalSettings, settings);

    // Feature-gate the ER data toggles on the user's action claims.
    // Driver Data requires both ER:View and Drivers' Hours; Maintenance Data requires ER:View, Checks and Fleet.
    const hasFleetView = UserHasAccess('FMS:View');
    const hasErView = UserHasAccess('ER:View');
    const hasDriversHoursView = UserHasAccess('DriversHours:View');
    const hasChecksView = UserHasAccess('Checks:View');
    const hasDriverDataAccess = hasErView && hasDriversHoursView;
    const hasMaintenanceDataAccess = hasErView && hasChecksView && hasFleetView;

    // Validation:
    //   - erNumber (Hub ER Identifier) is optional (backend treats it as always-optional now).
    //   - locations are required only when at least one of the data toggles is on.
    //   - both inspection-frequency dropdowns are always required.
    const eitherToggleOn = settings.sendDriverData || settings.sendMaintenanceData;
    const locationsError = eitherToggleOn && settings.locations.length === 0 ? 'This field is required.' : null;
    const vehicleFrequencyError = settings.oLicenceInspectionFrequencyWeeksVehicleDefault == null ? 'This field is required.' : null;
    const trailerFrequencyError = settings.oLicenceInspectionFrequencyWeeksTrailerDefault == null ? 'This field is required.' : null;
    // The DVSA ER Start Date is the operator's scheme-acceptance date; it is only meaningful, and so
    // only required, once a DVSA ER Number is set and a data type is being sent (mirrors the backend rule).
    const dvsaErStartDateError = eitherToggleOn && settings.dvsaErNumber !== '' && !settings.dvsaErStartDate ? 'This field is required.' : null;
    const isValid = !locationsError && !vehicleFrequencyError && !trailerFrequencyError && !dvsaErStartDateError;

    const dvsaErStartDateMinDate = moment().subtract(dvsaErStartDateMinYears, 'years').toDate();

    useEffect(initialLoad, []);

    // The backend rejects a DVSA ER Number when neither data type is enabled (PBI 162082), so
    // clear it whenever both toggles are off. Keyed on the raw toggle state — not feature access —
    // because the backend rule is about the toggles, not the user's claims.
    useEffect(() => {
        if (!settings.sendDriverData && !settings.sendMaintenanceData && settings.dvsaErNumber !== '') {
            setSettings(prev => ({ ...prev, dvsaErNumber: '' }));
        }
    }, [settings.sendDriverData, settings.sendMaintenanceData, settings.dvsaErNumber]);

    // The DVSA ER Start Date only exists alongside a DVSA ER Number, so clear it whenever the number
    // is empty (including when the toggles-off effect above clears the number) — mirrors the backend.
    useEffect(() => {
        if (settings.dvsaErNumber === '' && settings.dvsaErStartDate !== null) {
            setSettings(prev => ({ ...prev, dvsaErStartDate: null }));
        }
    }, [settings.dvsaErNumber, settings.dvsaErStartDate]);

    function getAvailableLocationIds(items) {
        return (items ?? []).flatMap(function (item) {
            return item.type === 'item' ? [item.id] : getAvailableLocationIds(item.items);
        });
    }

    function initialLoad() {
        const calls = [
            EarnedRecognitionService.getEarnedRecognition(),
            LocationService.getFormLocationSelectorCustomLocations(['Tacho', 'Vis', 'Audit'])
        ];

        // Skip the maintenance KPI validation call without FMS:View — the endpoint requires
        // that claim and would 403, blanking the whole page.
        if (hasFleetView) {
            calls.push(EarnedRecognitionService.validateMaintenanceKpi());
        }

        axios.all(calls).then(function ([erSettings, locationData, kpiValidation]) {
            const availableIds = getAvailableLocationIds(locationData?.location ?? []);
            const validLocations = (erSettings.locations ?? [])
                .map(l => l.id)
                .filter(id => availableIds.includes(id));

            const loaded = normaliseSettings({
                sendDriverData: erSettings.sendDriverData ?? false,
                sendMaintenanceData: erSettings.sendMaintenanceData ?? false,
                erNumber: erSettings.erNumber ?? '',
                locations: validLocations,
                dvsaErNumber: erSettings.dvsaErNumber ?? '',
                dvsaErStartDate: erSettings.dvsaErStartDate ?? null,
                oLicenceInspectionFrequencyWeeksVehicleDefault: erSettings.oLicenceInspectionFrequencyWeeksVehicleDefault ?? null,
                oLicenceInspectionFrequencyWeeksTrailerDefault: erSettings.oLicenceInspectionFrequencyWeeksTrailerDefault ?? null
            });

            setSettings(loaded);
            setOriginalSettings({ ...loaded });
            setCustomLocations(locationData);

            setPeriodStartDates({
                driverDataPeriodStart: erSettings.driverDataPeriodStart ?? null,
                maintenanceDataPeriodStart: erSettings.maintenanceDataPeriodStart ?? null,
                erNumberPeriodStart: erSettings.erNumberPeriodStart ?? null,
                driverDataStartPeriodStart: erSettings.driverDataStartPeriodStart ?? null,
                maintenanceDataStartPeriodStart: erSettings.maintenanceDataStartPeriodStart ?? null,
                erIdStartPeriodStart: erSettings.erIdStartPeriodStart ?? null
            });

            setInitialDriverDataEnabled(loaded.sendDriverData);
            setInitialMaintenanceDataEnabled(loaded.sendMaintenanceData);
            setInitialErNumber(loaded.erNumber);
            setInitialDvsaErNumber(loaded.dvsaErNumber);
            setInitialVehicleFrequency(loaded.oLicenceInspectionFrequencyWeeksVehicleDefault);
            setInitialTrailerFrequency(loaded.oLicenceInspectionFrequencyWeeksTrailerDefault);

            if (hasFleetView) {
                setMaintenanceKpiValidation(kpiValidation);
            }

            setLoading(false);
        }, () => {
            NotificationToaster.show(Intent.DANGER, 'Could not load Earned Recognition settings. Please refresh the page.');
            setLoading(false);
        });
    }

    function onDriverDataToggle(event) {
        setSettings(prev => ({ ...prev, sendDriverData: event.target.checked }));
    }

    function onMaintenanceDataToggle(event) {
        setSettings(prev => ({ ...prev, sendMaintenanceData: event.target.checked }));
    }

    function onDvsaErNumberChange(event) {
        setSettings(prev => ({ ...prev, dvsaErNumber: event.target.value }));
    }

    function dvsaErStartDateDisabledDays(date) {
        if (date == null) {
            return false;
        }
        return !isIsoPeriodStart(date);
    }

    function onDvsaErStartDateChange(selectedDate) {
        // Blueprint's DateInput accepts free-typed dates that bypass the calendar's disabled days, so
        // ignore anything that is not an ISO period start — the field can then only hold a valid start.
        const date = selectedDate == null ? null : moment(selectedDate).toDate();
        if (date != null && !isIsoPeriodStart(date)) {
            return;
        }

        setSettings(prev => ({ ...prev, dvsaErStartDate: date == null ? null : moment(date).format(dvsaErStartDateFormat) }));
    }

    function onPendingCancel() {
        // The only confirmation is the save-time one; cancelling leaves the settings as-is and saves nothing.
        setPendingConfirmation(null);
    }

    function onVehicleFrequencyChange(value) {
        // FormNumericInput emits "" when the field is cleared; coerce to null so the
        // required-field validation (== null) still treats empty as invalid.
        const numericValue = value === '' ? null : value;
        setSettings(prev => ({ ...prev, oLicenceInspectionFrequencyWeeksVehicleDefault: numericValue }));
    }

    function onTrailerFrequencyChange(value) {
        const numericValue = value === '' ? null : value;
        setSettings(prev => ({ ...prev, oLicenceInspectionFrequencyWeeksTrailerDefault: numericValue }));
    }

    function performSave() {
        setSaving(true);

        const payload = {
            sendDriverData: settings.sendDriverData,
            sendMaintenanceData: settings.sendMaintenanceData,
            erNumber: settings.erNumber,
            locations: settings.locations.map(id => ({ id })),
            dvsaErNumber: settings.dvsaErNumber === '' ? null : settings.dvsaErNumber,
            dvsaErStartDate: settings.dvsaErStartDate ? moment(settings.dvsaErStartDate).format(dvsaErStartDateFormat) : null,
            oLicenceInspectionFrequencyWeeksVehicleDefault: settings.oLicenceInspectionFrequencyWeeksVehicleDefault,
            oLicenceInspectionFrequencyWeeksTrailerDefault: settings.oLicenceInspectionFrequencyWeeksTrailerDefault
        };

        EarnedRecognitionService.saveEarnedRecognition(payload).then(
            () => {
                setOriginalSettings({ ...settings });
                setSaving(false);
                NotificationToaster.show(Intent.SUCCESS, 'Earned Recognition settings updated successfully.');
            },
            (error) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, error);
            }
        );
    }

    function proceedWithSave() {
        // Pre-save KPI validation (PBI 161527):
        // If either inspection-frequency value has changed from the originally loaded value,
        // re-validate against CheckMS with the new value(s). Only pass values that have changed —
        // omitting a parameter signals the backend should fall back to the DB default for that field.
        const vehicleChanged = settings.oLicenceInspectionFrequencyWeeksVehicleDefault !== initialVehicleFrequency;
        const trailerChanged = settings.oLicenceInspectionFrequencyWeeksTrailerDefault !== initialTrailerFrequency;

        // Without FMS:View the maintenance KPI controls are locked, so there is nothing to
        // re-validate against CheckMS — skip the pre-save validation and save directly.
        if (!hasFleetView || (!vehicleChanged && !trailerChanged)) {
            performSave();
            return;
        }

        setSaving(true);

        const vehicleParam = vehicleChanged ? settings.oLicenceInspectionFrequencyWeeksVehicleDefault : null;
        const trailerParam = trailerChanged ? settings.oLicenceInspectionFrequencyWeeksTrailerDefault : null;

        EarnedRecognitionService.validateMaintenanceKpi(vehicleParam, trailerParam).then(
            (result) => {
                if (result?.isValid === false) {
                    setSaving(false);
                    NotificationToaster.show(Intent.WARNING, result.reason);
                    return;
                }

                // Validation passed — proceed with the save. performSave manages its own setSaving lifecycle.
                setSaving(false);
                performSave();
            },
            (error) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, error);
            }
        );
    }

    function onSave() {
        // Save-time confirmation: removing any previously-active setting resets the ER data and loses
        // its history, so warn once before saving. A clean save (nothing removed) proceeds directly.
        const removingDriverData = initialDriverDataEnabled && !settings.sendDriverData;
        const removingMaintenanceData = initialMaintenanceDataEnabled && !settings.sendMaintenanceData;
        const removingHubEr = initialErNumber !== '' && settings.erNumber === '';
        const removingDvsaEr = initialDvsaErNumber !== '' && settings.dvsaErNumber === '';

        if (removingDriverData || removingMaintenanceData || removingHubEr || removingDvsaEr) {
            setPendingConfirmation({
                title: 'Please confirm',
                message: 'Removing these settings will reset the Earned Recognition data and all related ER history will be lost. Do you want to continue?',
                onConfirm: () => {
                    setPendingConfirmation(null);
                    proceedWithSave();
                }
            });
            return;
        }

        proceedWithSave();
    }

    const maintenanceKpiInvalid = maintenanceKpiValidation?.isValid === false;

    // When the user lacks one of the maintenance claims, the missing-claim NotificationInline below the
    // toggle covers the no-access case — so the danger helper only carries the KPI-validation reason.
    let maintenanceDangerHelperText = null;
    if (hasMaintenanceDataAccess && maintenanceKpiInvalid) {
        maintenanceDangerHelperText = maintenanceKpiValidation?.reason;
    }

    // Build the inline missing-claim messages, naming only the claims the user is actually missing.
    let driverDataAccessMessage = null;
    if (!hasDriverDataAccess) {
        const missingDriverClaims = [];
        if (!hasErView) {
            missingDriverClaims.push('Earned Recognition (ER:View)');
        }
        if (!hasDriversHoursView) {
            missingDriverClaims.push("Drivers' Hours (DriversHours:View)");
        }
        driverDataAccessMessage = `${missingDriverClaims.join(' and ')} access ${missingDriverClaims.length > 1 ? 'are' : 'is'} required to enable Driver Data.`;
    }

    let maintenanceDataAccessMessage = null;
    if (!hasMaintenanceDataAccess) {
        const missingMaintenanceClaims = [];
        if (!hasErView) {
            missingMaintenanceClaims.push('Earned Recognition (ER:View)');
        }
        if (!hasChecksView) {
            missingMaintenanceClaims.push('Checks (Checks:View)');
        }
        if (!hasFleetView) {
            missingMaintenanceClaims.push('Fleet (FMS:View)');
        }
        maintenanceDataAccessMessage = `${missingMaintenanceClaims.join(' and ')} access ${missingMaintenanceClaims.length > 1 ? 'are' : 'is'} required to enable Maintenance Data.`;
    }

    // The Hub/DVSA identifiers and Locations are only configurable when at least one data type is
    // both switched on AND accessible to this user — feature-gating an off toggle must not unlock them.
    const eitherDataActive = (settings.sendDriverData && hasDriverDataAccess) || (settings.sendMaintenanceData && hasMaintenanceDataAccess);

    const showDriverPeriodStart = settings.sendDriverData;
    const showMaintenancePeriodStart = settings.sendMaintenanceData;
    const driverPeriodStart = periodStartDates.driverDataPeriodStart ?? periodStartDates.driverDataStartPeriodStart;
    const maintenancePeriodStart = periodStartDates.maintenanceDataPeriodStart ?? periodStartDates.maintenanceDataStartPeriodStart;

    return (
        <div className="row">
            <h2>Earned Recognition</h2>

            <FormSwitch
                id="switch-send-driver-data"
                headingText="Enable Driver Data:"
                checked={settings.sendDriverData}
                loading={loading}
                disabled={saving || !hasDriverDataAccess}
                onChange={onDriverDataToggle}
            />
            <ShowHide
                evaluator={showDriverPeriodStart}
                show={(
                    <FormReadonly
                        headingText="Driver Data Period Start:"
                        value={formatPeriodStart(driverPeriodStart)}
                        loading={loading}
                    />
                )}
            />
            <NotificationInline
                intent="warning"
                allowClose={false}
                show={!hasDriverDataAccess}
                text={driverDataAccessMessage}
            />

            <FormSwitch
                id="switch-send-maintenance-data"
                headingText="Enable Maintenance Data:"
                checked={settings.sendMaintenanceData}
                loading={loading}
                disabled={saving || maintenanceKpiInvalid || !hasFleetView || !hasMaintenanceDataAccess}
                onChange={onMaintenanceDataToggle}
                dangerHelperText={maintenanceDangerHelperText}
            />
            <ShowHide
                evaluator={showMaintenancePeriodStart}
                show={(
                    <FormReadonly
                        headingText="Maintenance Data Period Start:"
                        value={formatPeriodStart(maintenancePeriodStart)}
                        loading={loading}
                    />
                )}
            />
            <NotificationInline
                intent="warning"
                allowClose={false}
                show={!hasMaintenanceDataAccess}
                text={maintenanceDataAccessMessage}
            />

            <NotificationInline
                intent="warning"
                allowClose={false}
                show={!eitherDataActive}
                text="Driver or Maintenance Data must be on to configure these settings."
            />

            <FormTextInput
                id="input-er-number"
                headingText="Hub Earned Recognition Identifier:"
                helperText="The reference from the old ER Hub. Kept for reference only; it does not affect what is reported or when reporting starts."
                placeholder="Enter ER ID"
                value={settings.erNumber}
                maxLength={15}
                loading={loading}
                disabled={saving || !eitherDataActive}
                onChange={(event) => setSettings({ ...settings, erNumber: event.target.value })}
            />

            <FormTextInput
                id="input-dvsa-er-number"
                headingText="DVSA Earned Recognition Identifier:"
                helperText="The operator's DVSA Earned Recognition number. Only set this once you are happy for KPIs to be sent to the DVSA. Setting it fixes the first period that will be reported."
                placeholder="Enter DVSA ER ID"
                value={settings.dvsaErNumber}
                maxLength={dvsaErNumberMaxLength}
                loading={loading}
                disabled={saving || !eitherDataActive}
                onChange={onDvsaErNumberChange}
            />

            <FormDateInput
                id="input-dvsa-er-start-date"
                headingText="DVSA Earned Recognition Start Date:"
                helperText="The date the operator joined the DVSA scheme. Used to work out the yearly anniversary for the MOT and trailer inspection pass rate report. Must be the first day of an ISO period."
                value={settings.dvsaErStartDate}
                minDate={dvsaErStartDateMinDate}
                firstDayOfWeek={1}
                modifiers={{ disabledDays: dvsaErStartDateDisabledDays }}
                loading={loading}
                disabled={saving || !eitherDataActive || settings.dvsaErNumber === ''}
                onChange={onDvsaErStartDateChange}
                dangerHelperText={dvsaErStartDateError}
            />
            <ShowHide
                evaluator={settings.dvsaErNumber !== ''}
                show={(
                    <FormReadonly
                        headingText="DVSA Earned Recognition Period Start:"
                        helperText="Set by the system, not entered. The first period Vision reports from, fixed when the DVSA identifier was added. Nothing before this period is reported to the DVSA."
                        value={formatPeriodStart(periodStartDates.erNumberPeriodStart ?? periodStartDates.erIdStartPeriodStart)}
                        loading={loading}
                    />
                )}
            />

            <FormLocationSelector
                businessArea=""
                useCustomLocations={true}
                customLocations={customLocations}
                selectedLocations={settings.locations}
                setSelectedLocations={(ids) => setSettings(prev => ({ ...prev, locations: ids }))}
                useHierarchy={useHierarchy}
                setUseHierarchy={setUseHierarchy}
                headingText="Locations:"
                loading={loading}
                disabled={saving || !eitherDataActive}
                dangerHelperText={locationsError}
            />

            <h3>Operator Licence Stated Inspection Frequency Weeks - Company Defaults</h3>
            <p>
                These fields are intended to act as default values for your account. If you have multiple Operator Licences with differing values then these must be entered <Link to="/operating-licences">here</Link>.
            </p>

            <FormNumericInput
                id="input-vehicle-inspection-frequency"
                headingText="Vehicle inspection frequency (weeks):"
                min={minFrequencyWeeks}
                max={maxFrequencyWeeks}
                allowDecimal={false}
                selectedValue={settings.oLicenceInspectionFrequencyWeeksVehicleDefault}
                onValueChange={onVehicleFrequencyChange}
                loading={loading}
                disabled={saving || !hasFleetView}
                dangerHelperText={vehicleFrequencyError}
            />

            <FormNumericInput
                id="input-trailer-inspection-frequency"
                headingText="Trailer inspection frequency (weeks):"
                min={minFrequencyWeeks}
                max={maxFrequencyWeeks}
                allowDecimal={false}
                selectedValue={settings.oLicenceInspectionFrequencyWeeksTrailerDefault}
                onValueChange={onTrailerFrequencyChange}
                loading={loading}
                disabled={saving || !hasFleetView}
                dangerHelperText={trailerFrequencyError}
            />

            <AlertUnsavedData isDirty={isDataDirty} />

            <ButtonSave
                onClick={onSave}
                disabled={saving}
                simpleDisabled={!isValid}
                loading={loading}
            />

            <AlertConfirm
                title={pendingConfirmation?.title ?? 'Please confirm'}
                isOpen={!!pendingConfirmation}
                onConfirm={() => pendingConfirmation?.onConfirm()}
                onCancel={onPendingCancel}
                confirmButtonIntent="primary"
            >
                <p>{pendingConfirmation?.message}</p>
            </AlertConfirm>
        </div>
    );
}
